import { CustomListMultiselect } from "components/shared/custom-list-multiselect";
import { AutosavingInput } from "components/ui/autosaving-input";
import {
    AdvancedSearchConditionNodeType,
    AdvancedSearchRelativeDateInterval,
    FieldConditionOperator,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useMemo } from "react";
import {
    customListKeyForEntityType,
    entityTypeStatusFilterLabel,
    hasInactiveClientTrigger,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

type StatusConditionChild =
    EditableWorkflow["trigger"]["schedule"]["entityCondition"]["children"][number]["children"][number];

const findInactiveClientStatusCondition = (workflow: EditableWorkflow) => {
    const entityCondition = workflow?.trigger?.schedule?.entityCondition;

    if (
        !entityCondition ||
        entityCondition.nodeType !== AdvancedSearchConditionNodeType.And
    ) {
        return null;
    }

    return entityCondition.children?.find(
        condition =>
            condition.nodeType === AdvancedSearchConditionNodeType.Or &&
            condition.children?.every(
                c =>
                    c.nodeType === AdvancedSearchConditionNodeType.FieldCondition &&
                    c.fieldName === "statusListItem",
            ),
    );
};

const findInactiveClientDateCondition = (workflow: EditableWorkflow) => {
    const entityCondition = workflow?.trigger?.schedule?.entityCondition;

    if (
        !entityCondition ||
        entityCondition.nodeType !== AdvancedSearchConditionNodeType.And
    ) {
        return null;
    }

    return entityCondition.children?.find(
        condition =>
            condition.nodeType === AdvancedSearchConditionNodeType.FieldCondition &&
            condition.fieldName === "updatedAt" &&
            typeof condition.relativeDate?.value === "number",
    );
};

interface InactiveClientTriggerEditorProps {
    workflow: EditableWorkflow;
    onChange: (newWorkflow: EditableWorkflow) => void;
}

export const InactiveClientTriggerEditor: React.FC<
    InactiveClientTriggerEditorProps
> = props => {
    const { workflow, onChange } = props;

    const clientStatuses = useMemo(() => {
        const statusCondition = findInactiveClientStatusCondition(workflow);

        if (!statusCondition) {
            return [];
        }

        return statusCondition.children
            .filter(
                condition =>
                    condition.nodeType ===
                        AdvancedSearchConditionNodeType.FieldCondition &&
                    condition.fieldName === "statusListItem",
            )
            .map(condition => condition.textValue);
    }, [workflow]);

    const handleStatusChange = useCallback(
        (newStatuses: string[]) => {
            const newWorkflow = produce(workflow, draft => {
                const statusCondition = findInactiveClientStatusCondition(draft);

                if (!statusCondition) {
                    return;
                }

                statusCondition.children = newStatuses.map(
                    (status): StatusConditionChild => ({
                        fieldName: "statusListItem",
                        textValue: status,
                        nodeType: AdvancedSearchConditionNodeType.FieldCondition,
                        operator: FieldConditionOperator.CustomListEquals,
                        dateValue: null,
                        calendarDateValue: null,
                        numberValue: null,
                        questionId: null,
                        promptId: null,
                        children: null,
                        relativeDate: null,
                    }),
                );
            });

            onChange(newWorkflow);
        },
        [workflow, onChange],
    );

    const daysForInactive = useMemo(() => {
        const dateCondition = findInactiveClientDateCondition(workflow);

        if (!dateCondition) {
            return 30;
        }

        return Math.abs(dateCondition.relativeDate.value);
    }, [workflow]);

    const handleDaysChange = useCallback(
        (newDays: string) => {
            const newDaysParsed = parseInt(newDays, 10);

            const newWorkflow = produce(workflow, draft => {
                const dateCondition = findInactiveClientDateCondition(draft);

                if (!dateCondition) {
                    return;
                }

                const newValue = isNaN(newDaysParsed)
                    ? Math.abs(dateCondition.relativeDate.value)
                    : newDaysParsed;

                dateCondition.relativeDate.interval =
                    AdvancedSearchRelativeDateInterval.Day;
                dateCondition.relativeDate.value = -newValue;
            });

            onChange(newWorkflow);
        },
        [workflow, onChange],
    );

    if (!hasInactiveClientTrigger(workflow)) {
        return null;
    }

    return (
        <div className="space-y-2">
            <div>
                <p>{entityTypeStatusFilterLabel(workflow.attachTo)}</p>
                <CustomListMultiselect
                    disabled={!workflow.regionVisibility?.editable}
                    customListKey={customListKeyForEntityType(workflow.attachTo)}
                    value={clientStatuses}
                    onChange={handleStatusChange}
                />
            </div>
            <div>
                <AutosavingInput
                    label="Days of inactivity"
                    type="number"
                    min={1}
                    initial={`${daysForInactive}`}
                    onCommit={handleDaysChange}
                    disabled={!workflow.regionVisibility?.editable}
                />
            </div>
        </div>
    );
};
